import React, { useMemo } from 'react';
import {
  Edit,
  TextField,
  ReferenceManyField,
  usePermissions,
  Labeled,
  TabbedShowLayout,
} from 'react-admin';

import {
  Card, CardContent, CardHeader, Divider, Grid,
} from '@mui/material';

import actions, { Actions } from '../../constants/actions';

import hasAccess from '../../utilities/hasAccess';

import ApiKeyField from './Fields/ApiKeyField';
import ApiKeyBreadcrumb from './ApiKeyBreadcrumb';
import Pagination from '../layout/Pagination';
import useValidateId from '../../hooks/useValidateId';
import resourceTypes from '../../constants/resourceTypes';
import EditReferenceArray from '../../customFields/EditReferenceArray';
import { apiKeyCleaner } from '../../utilities/dataProvider/cleaner';
import PermissionMissing from '../utils/PermissionMissing';
import resourceNames from '../../constants/resourceNames';
import EditToolbar from '../layout/EditToolbar';
import AuditDatagrid from '../audit/AuditDatagrid';
import AuditResourceList from '../audit/AuditResourceList';

const ApiKeyEdit = () => {
  const { permissions } = usePermissions<Actions[]>();
  const validatedId = useValidateId();

  const canEditPermissionRoles = useMemo(
    () => hasAccess(permissions, actions.PERMISSION_ROLE_LIST)
      && hasAccess(permissions, actions.API_KEY_UPDATE),
    [permissions],
  );

  if (!validatedId.isValid) return null;

  return (
    <Edit
      mutationMode="pessimistic"
      redirect={false}
      transform={apiKeyCleaner}
      actions={(<EditToolbar />)}
    >
      <ApiKeyBreadcrumb />
      <TabbedShowLayout>
        <TabbedShowLayout.Tab label="Information">
          <Grid container spacing={6}>
            <Grid size={12}>
              <Card variant="outlined">
                <CardHeader title="API Key" />
                <Divider />
                <CardContent>
                  <Grid container rowSpacing={2} columnSpacing={4}>
                    <Grid
                      container
                      spacing={2}
                      size={{
                        xs: 12,
                        md: 6,
                      }}
                    >
                      <Grid size={12}>
                        <Labeled label="Client ID">
                          <TextField source="clientId" />
                        </Labeled>
                      </Grid>
                      <Grid size={12}>
                        <Labeled label="Client secret">
                          {hasAccess(permissions, actions.API_KEY_GET_SECRET)
                            ? (<ApiKeyField />)
                            : (<PermissionMissing />)}
                        </Labeled>
                      </Grid>
                    </Grid>
                    {canEditPermissionRoles && (
                      <Grid
                        container
                        spacing={2}
                        size={{
                          xs: 12,
                          md: 6,
                        }}
                      >
                        <Grid size={12}>
                          <EditReferenceArray
                            source="permissionRoleIds"
                            showEdit
                            label="Permission Roles"
                            reference={resourceNames.PERMISSION_ROLES}
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabbedShowLayout.Tab>
        {hasAccess(permissions, actions.AUDIT_TRACK_LIST) && (
          <TabbedShowLayout.Tab label="Audit trail">
            <AuditResourceList resourceType={resourceTypes.API_KEY} />
          </TabbedShowLayout.Tab>
        )}
        {hasAccess(permissions, actions.AUDIT_TRACK_LIST) && (
          <TabbedShowLayout.Tab label="Activity">
            <Grid container>
              <Grid size={12}>
                <Card variant="outlined">
                  <ReferenceManyField
                    reference="audit-tracks"
                    target="authenticator.id"
                    sort={{ field: 'date', order: 'DESC' }}
                    perPage={15}
                    pagination={<Pagination disableEmptyText />}
                  >
                    <AuditDatagrid hideAuthenticatorColumns />
                  </ReferenceManyField>
                </Card>
              </Grid>
            </Grid>
          </TabbedShowLayout.Tab>
        )}
      </TabbedShowLayout>
    </Edit>
  );
};

export default ApiKeyEdit;
